import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit"
import {
   resetFacilityDetails,
   resetFacilityLevelSelectors,
} from "./facilitiesSlice"
import { resetLevelsConfigurations } from "./levelConfigurationsSlice"
import {
   addCustomer as addUserCostumer,
   removeCustomer as removeUserCustomer,
} from "./userSlice"
import { ApiError, ApiResponse } from "@/api"
import {
   CustomerResponse,
   requestCustomers,
   requestCreateCustomer,
   requestUpdateCustomer,
   requestDeleteCustomer,
} from "@/api/CustomersService"
import { HttpCodes } from "@/constants/globals"
import { Customer, CustomerForm } from "@/types/customer"
import { Token } from "@/types/index"
import { merge, remove } from "@/utils/lodash"

interface CustomersState {
   loading: boolean
   error: number | null
   customers: Customer[]
   loadingCustomer: boolean
   errorCustomer: number | null
}

export const initialState: CustomersState = {
   loading: false,
   error: null,
   customers: [],
   loadingCustomer: false,
   errorCustomer: null,
}

export const fetchCustomers = createAsyncThunk(
   "customers/fetchCustomers",
   async (_, thunkAPI) => {
      try {
         const response: ApiResponse<CustomerResponse[]> =
            await requestCustomers()
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

export const createCustomer = createAsyncThunk(
   "customers/createCustomer",
   async (customerData: CustomerForm, thunkAPI) => {
      try {
         const response: ApiResponse<CustomerResponse> =
            await requestCreateCustomer(customerData)

         if (response.status === HttpCodes.CREATED) {
            thunkAPI.dispatch(addUserCostumer(response.data))
         }
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

export const updateCustomer = createAsyncThunk(
   "customers/updateCustomer",
   async (
      {
         customerId,
         customerData,
      }: { customerId: string; customerData: CustomerForm },
      thunkAPI
   ) => {
      try {
         const response: ApiResponse<CustomerResponse> =
            await requestUpdateCustomer(customerId, customerData)
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

export const deleteCustomer = createAsyncThunk(
   "customers/deleteCustomer",
   async (customerId: string, thunkAPI) => {
      try {
         const response: ApiResponse<void> =
            await requestDeleteCustomer(customerId)
         if (response.status === HttpCodes.NO_CONTENT) {
            thunkAPI.dispatch(removeUserCustomer(customerId))
            const activeCustomerId = sessionStorage.getItem(Token.CUSTOMER_ID)
            if (activeCustomerId === customerId) {
               sessionStorage.removeItem(Token.CUSTOMER_ID)
               sessionStorage.removeItem(Token.FACILITY_ID)
               thunkAPI.dispatch(resetFacilityDetails())
               thunkAPI.dispatch(resetFacilityLevelSelectors())
               thunkAPI.dispatch(resetLevelsConfigurations())
            }
         }
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

const customersSlice = createSlice({
   name: "customers",
   initialState,
   reducers: {
      removeFacilityFromCustomer: (state, action) => {
         const { customerId, facilityId } = action.payload
         const customer = state.customers.find((c) => c.id === customerId)
         if (customer) {
            customer.facilities = customer.facilities.filter(
               (f) => f.id !== facilityId
            )
         }
      },
      addFacilityToCustomer: (state, action) => {
         const { customerId, facilityData } = action.payload
         const customer = state.customers.find((c) => c.id === customerId)
         if (customer) {
            customer.facilities.push(facilityData)
         }
      },
      addUserToFacility: (state, action) => {
         const { customerId, facilityId } = action.payload
         const customer = state.customers.find((c) => c.id === customerId)
         if (!customer) return
         const facility = customer.facilities.find((f) => f.id === facilityId)
         if (!facility) return
         if (!facility.users) {
            facility.users = 1
         } else {
            facility.users++
         }
      },
      updateFacility: (state, action) => {
         const { facilityId, facilityData } = action.payload
         const customer = state.customers.find((c) =>
            c.facilities.some((f) => f.id === facilityId)
         )
         if (customer) {
            const facilityIndex = customer.facilities.findIndex(
               (f) => f.id === facilityId
            )
            if (facilityIndex !== -1) {
               customer.facilities[facilityIndex] = merge(
                  {},
                  customer.facilities[facilityIndex],
                  facilityData
               )
            }
         }
      },
      updateFacilityByCustomer: (state, action) => {
         const { facilityId, facilityData } = action.payload
         const customer = state.customers.find((c) =>
            c.facilities.some((f) => f.id === facilityId)
         )
         if (customer) {
            const facilityIndex = customer.facilities.findIndex(
               (f) => f.id === facilityId
            )
            if (facilityIndex !== -1) {
               customer.name = facilityData.name
               customer.facilities[facilityIndex] = merge(
                  {},
                  customer.facilities[facilityIndex],
                  facilityData.facilities[0]
               )
            }
         }
      },
   },
   extraReducers: (builder) => {
      builder
         .addCase(fetchCustomers.pending, (state) => {
            state.loading = true
            state.error = null
         })
         .addCase(
            fetchCustomers.fulfilled,
            (state, action: PayloadAction<ApiResponse<CustomerResponse[]>>) => {
               state.loading = false
               state.customers = action.payload.data
            }
         )
         .addCase(fetchCustomers.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as number
         })
         .addCase(createCustomer.pending, (state) => {
            state.loadingCustomer = true
            state.errorCustomer = null
         })
         .addCase(createCustomer.fulfilled, (state, action) => {
            Object.assign(state.customers, [
               ...state.customers,
               action.payload.data,
            ])
            state.loadingCustomer = false
            state.errorCustomer = null
         })
         .addCase(createCustomer.rejected, (state, action) => {
            state.loadingCustomer = false
            state.errorCustomer = action.payload as number
         })
         .addCase(updateCustomer.pending, (state) => {
            state.loadingCustomer = true
            state.errorCustomer = null
         })
         .addCase(
            updateCustomer.fulfilled,
            (
               state,
               action: PayloadAction<
                  ApiResponse<CustomerResponse>,
                  string,
                  { arg: { customerId: string; customerData: CustomerForm } }
               >
            ) => {
               const { customerId } = action.meta.arg
               const updatedCustomer = action.payload.data
               const index = state.customers.findIndex(
                  (customer) => customer.id === customerId
               )
               if (index !== -1) {
                  state.customers[index] = merge(
                     {},
                     state.customers[index],
                     updatedCustomer
                  )
               }
               state.loadingCustomer = false
               state.errorCustomer = null
            }
         )
         .addCase(updateCustomer.rejected, (state, action) => {
            state.loadingCustomer = false
            state.errorCustomer = action.payload as number
         })
         .addCase(deleteCustomer.pending, (state) => {
            state.loadingCustomer = true
            state.errorCustomer = null
         })
         .addCase(deleteCustomer.fulfilled, (state, action) => {
            remove(state.customers, (c) => c.id === action.meta.arg)
            state.loadingCustomer = false
            state.errorCustomer = null
         })
         .addCase(deleteCustomer.rejected, (state, action) => {
            state.loadingCustomer = false
            state.errorCustomer = action.payload as number
         })
   },
})

export const {
   removeFacilityFromCustomer,
   addFacilityToCustomer,
   addUserToFacility,
   updateFacility,
   updateFacilityByCustomer,
} = customersSlice.actions

export default customersSlice.reducer
